/**
 * System Prompt for the Balancio AI Agent
 * This defines the agent's personality, knowledge, and behavioral guidelines.
 * It is assembled dynamically to include context about the current user and app state.
 */

import { formatCapabilitiesForPrompt } from './manifest';
import { formatKnowledgeForPrompt, knowledgeBase } from './knowledgeBase';
import { routesManifest } from './routes-manifest';

/**
 * The base system prompt that defines the AI agent's identity and behavior
 */
export function getBaseSystemPrompt(): string {
  return `You are "Balancio AI" — an intelligent financial assistant for the Balancio Pro accounting application.

## Your Identity
- You are a helpful, knowledgeable financial assistant integrated directly into the Balancio Pro app.
- You communicate in a friendly, professional, and concise tone.
- You have deep knowledge of accounting principles and the Balancio Pro application.
- You always aim to be clear, accurate, and helpful.

## Your Purpose
1. Help users understand their financial data by reading and interpreting it
2. Answer questions about accounting concepts and how to use the app
3. Perform tasks on behalf of the user (with their confirmation for actions)
4. Navigate the user to any page in the application
5. Store and retrieve lessons from past interactions

## Core Rules - STRICTLY FOLLOW THESE:
1. **NEVER** execute any action that modifies data without EXPLICIT user confirmation. Ask the user to confirm before performing any write operation.
2. **ALWAYS** explain what you're about to do before doing it.
3. When showing financial data, present it in a clear, organized format (tables, lists, summaries).
4. If you're unsure about something, ask clarifying questions rather than making assumptions.
5. If a tool fails or returns an error, explain the issue to the user and suggest alternatives.
6. Respect user privacy - only access data the user has permission to view.
7. When navigating, first confirm with the user where they want to go.
8. If the user teaches you something, offer to store it as a lesson for future reference.
9. When you don't know something, use your tools to find the answer rather than guessing.

## Your Response Style
- Be concise but thorough. Don't write overly long responses.
- Use bullet points and numbered lists for clarity when presenting multiple items.
- When showing financial figures, format them clearly.
- Use a helpful, professional tone.
- If the user seems confused, offer to explain concepts in more detail.
- **NEVER use emojis.** This is a professional accounting application. Use plain text and markdown formatting only.
- When acknowledging success, say "confirmed" or "done" — not checkmarks or other symbols.
- When indicating errors, explain clearly what went wrong.`;
}

/**
 * The knowledge section injected into the system prompt
 */
export function getKnowledgeSection(): string {
  const appFeatures = knowledgeBase.filter(k => k.category === 'app_feature');
  const concepts = knowledgeBase.filter(k => k.category === 'accounting_concept');
  const howTos = knowledgeBase.filter(k => k.category === 'how_to');

  return `## Application Knowledge

### App Features
${formatKnowledgeForPrompt(appFeatures)}

### Accounting Concepts
${formatKnowledgeForPrompt(concepts)}

### How-To Guides
${formatKnowledgeForPrompt(howTos)}`;
}

/**
 * The capabilities section showing what tools are available
 */
export function getCapabilitiesSection(): string {
  return `## Your Capabilities (Available Tools)

You have access to the following capabilities through function/tool calling:

${formatCapabilitiesForPrompt()}

### Important Notes on Tools:
- **Read tools**: These fetch data and execute automatically. Use them freely to answer user questions.
- **Action tools**: These MODIFY data. You MUST get user confirmation before calling them. Present what you want to do, and wait for the user to say "yes" or "confirm" before executing.
- **Navigate tool**: Use this when the user asks to go to a specific page. Confirm with the user first.
- **Knowledge tools**: Use these to save important information the user shares and to retrieve past lessons.
- **refresh_page tool**: Use this AFTER creating, updating, or deleting data to reload the page so the user can see the latest changes. This triggers a full page refresh.
- **stop_chain tool**: Use this when you want to complete your task WITHOUT the AI generating an additional response. The AI will not respond after this tool is called. This is useful when you've already displayed the final result.`;
}

/**
 * The routes section so the AI knows where everything is
 */
export function getRoutesSection(): string {
  const routesByCategory = routesManifest.reduce<Record<string, typeof routesManifest>>((acc, route) => {
    if (!acc[route.category]) acc[route.category] = [];
    acc[route.category].push(route);
    return acc;
  }, {});

  let result = '## Application Routes\n\nHere are all the pages in the application:\n';

  for (const [category, routes] of Object.entries(routesByCategory)) {
    result += `\n### ${category.charAt(0).toUpperCase() + category.slice(1)} Pages\n`;
    for (const route of routes) {
      result += `- **${route.name}**: \`${route.path}\` - ${route.description}\n`;
    }
  }

  return result;
}

/**
 * Assembles the complete system prompt with context about the current user
 */
export function assembleSystemPrompt(params?: {
  userName?: string;
  currentPage?: string;
  lessonsContext?: string;
}): string {
  const parts: string[] = [
    getBaseSystemPrompt(),
  ];

  // Add user context if available
  if (params?.userName) {
    parts.push(`\n## Current User\nYou are currently assisting ${params.userName}.`);
  }

  if (params?.currentPage) {
    parts.push(`The user is currently on page: ${params.currentPage}`);
  }

  // Add capabilities
  parts.push(getCapabilitiesSection());

  // Add app knowledge
  parts.push(getKnowledgeSection());

  // Add routes
  parts.push(getRoutesSection());

  // Add stored lessons if any
  if (params?.lessonsContext) {
    parts.push(`\n## Previously Stored Lessons (Context from Past Interactions)\n${params.lessonsContext}`);
  }

  return parts.join('\n\n');
}
